import { format } from 'date-fns';
import type { SchedulingUnavailability, Shift } from '../types';

function parseHm(date: Date, hm?: string | null): Date | null {
  if (!hm) return null;
  const [h, m] = hm.split(':').map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  return new Date(date.getFullYear(), date.getMonth(), date.getDate(), h, m, 0);
}

export function formatUnavailabilityBanner(item: SchedulingUnavailability): string {
  const time = item.isAllDay || !item.startTime || !item.endTime
    ? 'Unavailable'
    : `Unavailable ${item.startTime.slice(0, 5)}–${item.endTime.slice(0, 5)}`;
  if (item.status === 'Pending') return `${time} · Pending`;
  return time;
}

export function unavailabilityTooltip(item: SchedulingUnavailability): string {
  const parts = [item.reason?.trim() || 'Unavailable'];
  if (item.status === 'Pending') parts.push('Awaiting approval');
  else if (item.approvedByName) {
    const when = item.approvedAt ? ` · ${format(new Date(item.approvedAt), 'dd MMM')}` : '';
    parts.push(`Approved by ${item.approvedByName}${when}`);
  } else {
    parts.push('Approved');
  }
  return parts.join(' — ');
}

export function shiftOverlapsUnavailability(
  item: SchedulingUnavailability,
  shiftStart: Date,
  shiftEnd: Date
): boolean {
  const day = new Date(shiftStart.getFullYear(), shiftStart.getMonth(), shiftStart.getDate());
  if (item.isAllDay) {
    const dayEnd = new Date(day);
    dayEnd.setDate(dayEnd.getDate() + 1);
    return shiftStart < dayEnd && day <= shiftEnd;
  }
  const windowStart = parseHm(day, item.startTime);
  const windowEnd = parseHm(day, item.endTime);
  if (!windowStart || !windowEnd) return false;
  return shiftStart < windowEnd && windowStart < shiftEnd;
}

export function approvedUnavailabilityBlocksShift(
  items: SchedulingUnavailability[] | undefined,
  shiftStart: Date,
  shiftEnd: Date
): string | null {
  for (const item of items ?? []) {
    if (item.status !== 'Approved') continue;
    if (!shiftOverlapsUnavailability(item, shiftStart, shiftEnd)) continue;
    if (item.isAllDay) return 'Cannot create shift: employee has approved unavailability on this day.';
    return `Cannot create shift: employee is unavailable ${item.startTime?.slice(0, 5)}–${item.endTime?.slice(0, 5)}.`;
  }
  return null;
}

export function shiftConflictsWithUnavailability(
  items: SchedulingUnavailability[] | undefined,
  shift: Shift
): boolean {
  const start = new Date(shift.startTime);
  const end = new Date(shift.endTime);
  return (items ?? []).some(
    (item) => item.status === 'Approved' && shiftOverlapsUnavailability(item, start, end)
  );
}
